/*
 * BrandingSlotHelper — visual guidance below each branding upload field.
 *
 * Solves the "seller uploads logo blind" problem identified Feb 2026:
 *   - What dimensions should I make this?
 *   - PNG, JPG, or WEBP — which is right for THIS slot?
 *   - How will it actually render at the target surface (favicon, hero,
 *     sidebar, email)?
 *
 * Each slot gets 3 pieces of help, laid out in a single tight strip:
 *   1. Spec card — Dimensions · Format · Max size · Transparency
 *   2. Inline-SVG example showing the ideal composition (no external deps)
 *   3. Live thumbnail of the CURRENT upload at its actual render size
 *
 * No external images, no API calls — pure inline SVG + Tailwind.
 *
 * Usage:
 *   <BrandingSlotHelper variant="logo_primary"   currentUrl={form.logo_url}        primary={form.primary_color} accent={form.accent_color} name={form.name} />
 *   <BrandingSlotHelper variant="logo_square"    currentUrl={form.logo_square_url} primary={form.primary_color} accent={form.accent_color} name={form.name} />
 *   <BrandingSlotHelper variant="logo_dark"      currentUrl={form.logo_dark_url}   primary={form.primary_color} accent={form.accent_color} name={form.name} />
 *   <BrandingSlotHelper variant="cover_image"    currentUrl={form.cover_image_url} primary={form.primary_color} />
 */
import React from "react";

// Per-variant config: copy + recommended specs + SVG example renderer.
const VARIANTS = {
  logo_primary: {
    label:        "Logo · primary",
    dimensions:   "1200 × 400 px (3:1)",
    format:       "PNG",
    formatHint:   "transparent background",
    maxSize:      "2 MB",
    transparency: true,
    purpose:      "Sidebar, invoice header, email header, public profile",
    formatExplanation:
      "PNG handles sharp logo edges and text perfectly. A transparent " +
      "background means your mark sits cleanly on every surface — the " +
      "dark hero, the white invoice, the buyer portal. JPG bakes a " +
      "background (usually white) which looks broken on the dark hero.",
  },
  logo_square: {
    label:        "Logo · square (monogram)",
    dimensions:   "512 × 512 px (1:1)",
    format:       "PNG",
    formatHint:   "transparent background",
    maxSize:      "1 MB",
    transparency: true,
    purpose:      "Browser tab favicon (16×16), round chips, mobile icon",
    formatExplanation:
      "This is the tightest slot — it's rendered as a 16×16 favicon. " +
      "Use just your monogram / icon (not the wordmark). A wide " +
      "horizontal logo gets squished into an unrecognizable sliver here.",
  },
  logo_dark: {
    label:        "Logo · dark mode",
    dimensions:   "1200 × 400 px (match primary)",
    format:       "PNG",
    formatHint:   "transparent background, light artwork",
    maxSize:      "2 MB",
    transparency: true,
    purpose:      "Dark surfaces (storefront hero, dark sidebar)",
    formatExplanation:
      "Only needed if your primary logo has dark artwork (black/dark " +
      "text) that disappears on the dark storefront hero. Upload a " +
      "white/light version on transparent background here.",
  },
  cover_image: {
    label:        "Cover image",
    dimensions:   "1600 × 400 px (4:1)",
    format:       "JPG",
    formatHint:   "or WEBP for smaller file size",
    maxSize:      "5 MB",
    transparency: false,
    purpose:      "Hero background on your storefront",
    formatExplanation:
      "JPG is best for photographs — natural images compress to ~1/4 " +
      "the size of PNG with no visible quality loss. Use a lifestyle " +
      "photo of your product in context, NOT a logo lockup. The hero " +
      "applies a primary-color gradient on top, so the photo can be " +
      "darker or busier than you'd think — text stays legible.",
  },
};

// ─── Inline SVG examples ──────────────────────────────────────────────────
// Each renders a small abstract mockup showing the ideal composition for
// that slot. Pure SVG — no images, no external deps.

const PrimaryLogoExample = ({ primary = "#0F766E", accent = "#FCD34D" }) => (
  <svg viewBox="0 0 240 80" className="w-full h-12" preserveAspectRatio="xMidYMid meet">
    <defs>
      <pattern id="checkered-bg-1" width="10" height="10" patternUnits="userSpaceOnUse">
        <rect width="10" height="10" fill="#fff"/>
        <rect width="5" height="5" fill="#f4f4f5"/>
        <rect x="5" y="5" width="5" height="5" fill="#f4f4f5"/>
      </pattern>
    </defs>
    <rect width="240" height="80" fill="url(#checkered-bg-1)" rx="6"/>
    <g transform="translate(20 22)">
      <rect width="36" height="36" rx="8" fill={primary}/>
      <text x="18" y="25" textAnchor="middle" fontSize="22" fontFamily="-apple-system, sans-serif" fontWeight="700" fill={accent}>B</text>
      <text x="48" y="25" fontSize="20" fontFamily="-apple-system, sans-serif" fontWeight="700" fill="#0F172A">BRAND</text>
      <text x="48" y="33" fontSize="6" letterSpacing="0.12em" fontFamily="-apple-system, sans-serif" fontWeight="600" fill="#71717A">WHOLESALE</text>
    </g>
  </svg>
);

const SquareLogoExample = ({ primary = "#0F766E", accent = "#FCD34D" }) => (
  <svg viewBox="0 0 80 80" className="w-12 h-12" preserveAspectRatio="xMidYMid meet">
    <defs>
      <pattern id="checkered-bg-2" width="10" height="10" patternUnits="userSpaceOnUse">
        <rect width="10" height="10" fill="#fff"/>
        <rect width="5" height="5" fill="#f4f4f5"/>
        <rect x="5" y="5" width="5" height="5" fill="#f4f4f5"/>
      </pattern>
    </defs>
    <rect width="80" height="80" fill="url(#checkered-bg-2)" rx="6"/>
    <rect x="14" y="14" width="52" height="52" rx="10" fill={primary}/>
    <text x="40" y="55" textAnchor="middle" fontSize="36" fontFamily="-apple-system, sans-serif" fontWeight="800" fill={accent}>B</text>
  </svg>
);

const DarkLogoExample = ({ primary = "#0F766E" }) => (
  <svg viewBox="0 0 240 80" className="w-full h-12" preserveAspectRatio="xMidYMid meet">
    <rect width="240" height="80" fill={primary} rx="6"/>
    <g transform="translate(20 22)">
      <rect width="36" height="36" rx="8" fill="#fff" fillOpacity="0.14"/>
      <text x="18" y="25" textAnchor="middle" fontSize="22" fontFamily="-apple-system, sans-serif" fontWeight="700" fill="#fff">B</text>
      <text x="48" y="25" fontSize="20" fontFamily="-apple-system, sans-serif" fontWeight="700" fill="#fff">BRAND</text>
      <text x="48" y="33" fontSize="6" letterSpacing="0.12em" fontFamily="-apple-system, sans-serif" fontWeight="600" fill="#ffffffaa">WHOLESALE</text>
    </g>
  </svg>
);

const CoverExample = ({ primary = "#0F766E" }) => (
  <svg viewBox="0 0 320 80" className="w-full h-12" preserveAspectRatio="none">
    <defs>
      <linearGradient id="cover-photo" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%"  stopColor="#fbbf24"/>
        <stop offset="40%" stopColor="#f97316"/>
        <stop offset="80%" stopColor="#7c3aed"/>
        <stop offset="100%" stopColor="#1e293b"/>
      </linearGradient>
      <linearGradient id="cover-overlay" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%"   stopColor={primary} stopOpacity="0.95"/>
        <stop offset="55%"  stopColor={primary} stopOpacity="0.66"/>
        <stop offset="100%" stopColor={primary} stopOpacity="0.4"/>
      </linearGradient>
    </defs>
    <rect width="320" height="80" fill="url(#cover-photo)" rx="6"/>
    <rect width="320" height="80" fill="url(#cover-overlay)" rx="6"/>
    <circle cx="40" cy="40" r="3" fill="#fff" fillOpacity="0.4"/>
    <circle cx="70" cy="32" r="2" fill="#fff" fillOpacity="0.3"/>
    <circle cx="100" cy="48" r="2.5" fill="#fff" fillOpacity="0.25"/>
  </svg>
);

const EXAMPLE_BY_VARIANT = {
  logo_primary: PrimaryLogoExample,
  logo_square:  SquareLogoExample,
  logo_dark:    DarkLogoExample,
  cover_image:  CoverExample,
};

// ─── Live thumbnail (current upload, rendered at target surface size) ────
function LiveThumbnail({ variant, currentUrl, primary, accent, name }) {
  if (!currentUrl) {
    return (
      <div
        className="text-[10px] text-foreground/45 italic px-2 py-1 rounded border border-dashed border-foreground/15 bg-white"
        data-testid={`bsh-${variant}-live-empty`}
      >
        Nothing uploaded yet
      </div>
    );
  }
  const initial = (name || "?").charAt(0).toUpperCase();

  if (variant === "logo_primary") {
    // Renders the way it shows in the seller's sidebar header — small
    // and wide-permissive.
    return (
      <div className="flex items-center gap-3" data-testid={`bsh-${variant}-live`}>
        <span className="text-[10px] mono uppercase tracking-wider text-foreground/45">Sidebar</span>
        <div className="bg-white border border-foreground/10 rounded-md px-3 py-2 inline-flex items-center">
          <img src={currentUrl} alt="" className="max-h-7 max-w-[10rem] object-contain object-left"/>
        </div>
      </div>
    );
  }
  if (variant === "logo_square") {
    // Show the ACTUAL favicon size (16px) + a magnified version side-by-side.
    return (
      <div className="flex items-center gap-3" data-testid={`bsh-${variant}-live`}>
        <span className="text-[10px] mono uppercase tracking-wider text-foreground/45">Browser tab</span>
        <img src={currentUrl} alt="" className="w-4 h-4 object-cover rounded-[2px] border border-foreground/15"/>
        <span className="text-[10px] mono uppercase tracking-wider text-foreground/45">Profile chip</span>
        <img src={currentUrl} alt="" className="w-9 h-9 object-cover rounded-lg border border-foreground/15"/>
      </div>
    );
  }
  if (variant === "logo_dark") {
    // Render on the seller's primary color background — same surface
    // where this variant will actually appear (the dark storefront hero).
    return (
      <div className="flex items-center gap-3" data-testid={`bsh-${variant}-live`}>
        <span className="text-[10px] mono uppercase tracking-wider text-foreground/45">Dark hero</span>
        <div className="rounded-md px-3 py-2 inline-flex items-center" style={{ background: primary || "#0a1f1c" }}>
          <img src={currentUrl} alt="" className="max-h-7 max-w-[10rem] object-contain object-left"/>
        </div>
      </div>
    );
  }
  if (variant === "cover_image") {
    // Render with the actual gradient overlay used on /buyer/storefront —
    // so the seller sees how the photo will actually look as a hero,
    // not as a raw photo.
    const tint = primary || "#0a1f1c";
    return (
      <div className="space-y-1" data-testid={`bsh-${variant}-live`}>
        <div className="flex items-center gap-2 text-[10px] mono uppercase tracking-wider text-foreground/45">
          Hero — actual storefront overlay
        </div>
        <div
          className="relative rounded-lg overflow-hidden border border-foreground/10 h-14"
          style={{
            backgroundImage: `linear-gradient(135deg, ${tint}f2 0%, ${tint}a8 55%, ${tint}66 100%), url('${currentUrl}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 flex items-center px-3 gap-2 text-white">
            <span
              className="w-5 h-5 rounded grid place-items-center text-[10px] font-bold display"
              style={{ background: accent || "#FCD34D", color: tint }}
            >
              {initial}
            </span>
            <span className="text-[10px] font-medium">{name || "Your brand"}</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
}

// ─── Helper block (spec card + example + live thumbnail) ────────────────
export default function BrandingSlotHelper({
  variant,
  currentUrl,
  primary = "#0F766E",
  accent = "#FCD34D",
  name = "Your brand",
}) {
  const cfg = VARIANTS[variant];
  if (!cfg) return null;
  const ExampleSvg = EXAMPLE_BY_VARIANT[variant];

  return (
    <div
      className="mt-3 rounded-xl border border-foreground/10 bg-foreground/[0.02] p-4 space-y-3"
      data-testid={`branding-slot-helper-${variant}`}
    >
      {/* Spec strip — Dimensions · Format · Max size · Transparency */}
      <div className="flex items-center flex-wrap gap-x-5 gap-y-1.5 text-[11px]">
        <div className="flex items-center gap-1.5">
          <span className="mono uppercase tracking-wider text-foreground/45">Recommended</span>
          <span className="font-medium text-foreground/85">{cfg.dimensions}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="mono uppercase tracking-wider text-foreground/45">Format</span>
          <span className="font-medium text-foreground/85">{cfg.format}</span>
          <span className="text-foreground/55">({cfg.formatHint})</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="mono uppercase tracking-wider text-foreground/45">Max</span>
          <span className="font-medium text-foreground/85">{cfg.maxSize}</span>
        </div>
        {cfg.transparency && (
          <span className="inline-flex items-center gap-1 text-emerald-700 text-[10px] mono uppercase tracking-wider">
            ✓ Transparency
          </span>
        )}
      </div>

      {/* Example + Live thumbnail row */}
      <div className="grid sm:grid-cols-2 gap-3">
        {/* Example: do this */}
        <div className="rounded-lg border border-foreground/10 bg-white px-3 py-2.5">
          <div className="text-[10px] mono uppercase tracking-wider text-foreground/45 mb-1.5">
            Example
          </div>
          <div className="flex items-center justify-center">
            <ExampleSvg primary={primary} accent={accent} />
          </div>
        </div>
        {/* Live: what you've uploaded */}
        <div className="rounded-lg border border-foreground/10 bg-white px-3 py-2.5">
          <div className="text-[10px] mono uppercase tracking-wider text-foreground/45 mb-1.5">
            Yours — live preview
          </div>
          <div className="flex items-center min-h-[2.5rem]">
            <LiveThumbnail
              variant={variant}
              currentUrl={currentUrl}
              primary={primary}
              accent={accent}
              name={name}
            />
          </div>
        </div>
      </div>

      {/* Format explanation — plain-english why PNG vs JPG */}
      <p className="text-[11.5px] text-foreground/65 leading-snug">
        <span className="font-medium text-foreground/85">Purpose:</span> {cfg.purpose}.
        <br/>
        {cfg.formatExplanation}
      </p>
    </div>
  );
}
